import React from 'react';
import { Calendar, Award, TrendingUp } from 'lucide-react';
import './About.css';

const About = () => {
  const milestones = [
    {
      year: '2025',
      title: 'Company Founded',
      description: 'AG&WESTERN Rail Inc. established on December 23, 2025 in Richmond, Virginia by Aaron Gregory to revolutionize rail transportation.'
    },
    {
      year: '2025',
      title: 'First Passenger Routes',
      description: 'Launched three flagship passenger services: The Cruiser, The Libertarian, and The Southsidean, connecting communities across the East Coast.'
    },
    {
      year: '2025',
      title: 'Thru-Way Connections',
      description: 'Introduced complimentary bus service connecting passengers to stations and providing backup transportation during service interruptions.'
    },
    {
      year: '2026',
      title: 'Expansion Plans',
      description: 'Planning to provide rail opportunities to Amtrak passengers, helping them avoid delayed routes with reliable alternatives.'
    }
  ];

  return (
    <div className="about">
      {/* Video Section */}
      <section className="about-video-section">
        <div className="container">
          <video 
            className="about-intro-video"
            autoPlay 
            muted 
            loop 
            playsInline
          >
            <source src="/agwestern_intro.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </section>

      {/* Hero Section */}
      <section className="about-hero section-dark">
        <div className="container">
          <div className="about-hero-content">
            <h1>About AG&WESTERN Rail Inc.</h1>
            <p className="about-subtitle">RELAX. EXPLORE. LIVE.</p>
            <p className="about-intro">
              Founded on December 23, 2025, AG&WESTERN Rail Inc. was created to address 
              the pressing need for efficient transportation solutions in both freight and 
              passenger services across the Eastern United States.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-content">
              <h2>Our Mission</h2>
              <p>
                AG&WESTERN Rail Inc. specializes in intermodal freight transport, managing 
                private cargo alongside short lines for major companies. Our main line passenger 
                trains connect small towns to the largest cities, ensuring on-time arrivals and 
                enhanced passenger satisfaction.
              </p>
              <p>
                We operate scheduled passenger trains on a mainline separate from freight, ensuring 
                punctual service. Our vision includes providing rail opportunities to Amtrak passengers, 
                helping them avoid delayed routes by offering reliable alternative services.
              </p>
              <p>
                AG&W's guiding principle is <strong>RELAX. EXPLORE. LIVE.</strong> — making rail 
                travel comfortable, reliable, and accessible to all.
              </p>
            </div>
            <div className="mission-features">
              <div className="feature-item">
                <Calendar size={32} />
                <h4>Founded December 2025</h4>
                <p>A new vision for American rail transportation</p>
              </div>
              <div className="feature-item">
                <Award size={32} />
                <h4>Service Excellence</h4>
                <p>Committed to on-time performance and passenger satisfaction</p>
              </div>
              <div className="feature-item">
                <TrendingUp size={32} />
                <h4>Rapid Growth</h4>
                <p>Expanding routes and services to serve more communities</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="section section-gray">
        <div className="container">
          <div className="founder-section">
            <div className="founder-image-placeholder">
              <img 
                src="https://customer-assets.emergentagent.com/job_western-rail-hub/artifacts/8zsbr2t4_ChatGPT%20Image%20May%2024%2C%202026%2C%2009_20_28%20PM.png"
                alt="Aaron Gregory - Founder & CEO"
                className="founder-photo-image"
              />
            </div>
            <div className="founder-content">
              <h2>Meet Our Founder</h2>
              <h3>Aaron Gregory</h3>
              <p className="founder-title">Founder & Chief Executive Officer</p>
              <p>
                Aaron Gregory founded AG&WESTERN Rail Inc. on December 23, 2025, with a bold 
                vision to transform rail transportation in America. His commitment to operational 
                excellence and passenger-first service has been the driving force behind the 
                company's innovative approach to connecting communities.
              </p>
              <p>
                Under Aaron's leadership, AG&WESTERN has established three flagship passenger 
                routes and introduced the groundbreaking Thru-Way Connections service, ensuring 
                passengers never get stranded. His philosophy of "RELAX. EXPLORE. LIVE." embodies 
                the company's dedication to making rail travel accessible, comfortable, and reliable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Our Journey</h2>
            <p>Key milestones in our company's history</p>
          </div>
          <div className="timeline">
            {milestones.map((milestone) => (
              <div key={`${milestone.year}-${milestone.title}`} className="timeline-item">
                <div className="timeline-year">{milestone.year}</div>
                <div className="timeline-content">
                  <h3>{milestone.title}</h3>
                  <p>{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <h2>Our Values</h2>
            <p>The principles that guide everything we do</p>
          </div>
          <div className="values-grid">
            <div className="value-card">
              <h3>Reliability</h3>
              <p>We maintain the highest standards of on-time performance and service consistency.</p>
            </div>
            <div className="value-card">
              <h3>Safety</h3>
              <p>The safety of our passengers and cargo is our top priority in every operation.</p>
            </div>
            <div className="value-card">
              <h3>Innovation</h3>
              <p>We continuously improve our services through technology and operational excellence.</p>
            </div>
            <div className="value-card">
              <h3>Community</h3>
              <p>We connect communities and support local economies through our rail network.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;